'use client';

import { useTranslations } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import { MdArrowBackIos } from "react-icons/md";
import { useLocale } from "next-intl";
import useFetchProducts from "@/components/products/fetcherProduct";
import { useRouter } from 'next/navigation'; // Import useRouter
import Newshop from "@/components/home-page/body/shopLetterOtherPages";
import Offer from "@/components/home-page/body/offer";
import { HiTrendingDown } from "react-icons/hi";
import StarRating from "@/components/products/starsComponent";
import useFetchReviews from "@/components/user-dashboard/FetcherReviews";
// Define the reduction function at the top
const reduction = (old: any, new_p: any) => {
  if (old === null || old === 0) return 0; // Handle null or zero old_price
  return ~~(((old - new_p) / old) * 100);
};

export default function Products() {
  const t = useTranslations('Shop');
  const tm = useTranslations('Shop-id');
  const l = useLocale();
  const router = useRouter(); // Initialize the router
  const { reviews} = useFetchReviews();
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState(""); // State for selected filter
  const itemsPerPage = 32;
  const { Products, error, isLoading } = useFetchProducts();

  // Filter products where is_visible is true
  const visibleProducts = Products.filter((product: any) => product.is_visible === true);

  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(e.target.value);
    setCurrentPage(1); // Reset to the first page when filter changes
  };

  // Sort products based on the selected filter
  const sortedProducts = [...visibleProducts].sort((a:any, b:any) => {
    if (filter === "lower_price") {
      return a.new_price - b.new_price; // Sort by lower price
    } else if (filter === "higher_price") {
      return b.new_price - a.new_price; // Sort by higher price
    } else if (filter === "reduction") {
      const reductionA = reduction(a.old_price, a.new_price);
      const reductionB = reduction(b.old_price, b.new_price);
      return reductionB - reductionA; // Sort by higher reduction
    } else {
      return 0; // No sorting
    }
  });

  // Calculate total number of pages
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  // Get the items for the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedProducts.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle next page
  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle previous page
  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Get localized field
  const getLocalizedField = (item: any, field: string) => item[`${field}_${l}`] || item[`${field}_en`];

  // Skeleton Loading Component
  const SkeletonLoader = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 custom:grid-cols-4 pb-10 gap-1 custom:gap-2">
      {Array.from({ length: itemsPerPage }).map((_, index) => (
        <div key={index} className="cursor-pointer hover:shadow-2xl hover:bg-secondary hover:text-yel group">
          <div className="h-72 w-full relative bg-gray-300 animate-pulse"></div>
          <div className="py-3 mb-3 flex flex-col gap-3 group-hover:px-3">
            <div className="h-6 bg-gray-300 animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-300 animate-pulse w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // No products message
  if (!isLoading && visibleProducts.length === 0) {
    return (
      <div>
        {/* Hero Section */}
        <div className="h-48 sm:h-60 sm:col-span-1 relative flex items-center m-1 lg:m-3 rounded-xl bg-secondary">
          <Image
            src="/h.avif"
            alt={t('tag')}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            style={{ objectFit: "cover" }}
            className="z-0 rounded-xl"
            priority
          />
          <div className="absolute inset-0 bg-black bg-opacity-60 rounded-xl"></div>
          <div className="absolute sm:bottom-10 mx-5 lg:mx-10 px-1 shadow-lg">
            <h1 className="text-xl md:text-2xl font-bold uppercase text-yel mb-3 xl:mb-8">
              {t('Latest')}
            </h1>
            <p className="max-w-3xl text-yel text-sm sm:text-lg">
              {t('description')}
            </p>
          </div>
        </div>

        {/* No Products Message */}
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-600 mb-4">{tm("No Products Available")}</h2>
            <p className="text-gray-500">{tm("Check back later")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div className="h-48 sm:h-60 sm:col-span-1 relative flex items-center m-1 lg:m-3 rounded-xl bg-secondary">
        <Image
          src="/h.png"
          alt={t('tag')}
          fill
          sizes="(max-width: 768px) 100vw, 50vw"
          style={{ objectFit: "cover" }}
          className="z-0 rounded-xl"
          priority
        />
        <div className="absolute inset-0 bg-black bg-opacity-60 rounded-xl"></div>
        <div className="absolute sm:bottom-10 mx-5 lg:mx-10 px-1 shadow-lg">
          <h1 className="text-xl md:text-2xl font-bold uppercase text-yel mb-3 xl:mb-8">
            {t('Latest')}
          </h1>
          <p className="max-w-3xl text-yel text-sm sm:text-lg">
            {t('description')}
          </p>
        </div>
      </div>

      {/* Latest News Section */}
      <div>
        <div className="w-full px-1 custom:px-4 space-y-8 pb-10">
          <div className="flex justify-between items-center flex-wrap ">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase py-6"></h2>
            <div>
              <label htmlFor="filter" className="mr-2"> {t('Filter')}:{" "}</label>
              <select
                id="filter"
                value={filter}
                onChange={handleFilterChange}
                className="px-2 py-1 border rounded bg-b text-yel "
              >
                <option value="" className="text-yel">{t('None')}</option>
                <option value="lower_price" className="text-yel">{t('Lower')}</option>
                <option value="higher_price" className="text-yel">{t('Higher')}</option>
                <option value="reduction" className="text-yel">{t('Reductions')}</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <SkeletonLoader />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4  pb-10 gap-1 custom:gap-2 ">
              {currentItems.map((item) => (
                <div key={item.id} className="cursor-pointer border border-1 rounded-lg overflow-hidden hover:shadow-2xl bg-amber-50 hover:bg-b hover:text-yel group" 
                onClick={() => router.push(`/${l}/product/${getLocalizedField(item, 'url')}`)}>

                  <div className="h-60 sm:h-80  md:h-96 2xl:h-[500px]  w-auto relative ">
                    <Image
                     src={`${process.env.NEXT_PUBLIC_IMAGE}/${item.image}`}
                      alt={getLocalizedField(item, "tag") || 'tag'}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      style={{ objectFit: "cover" }}
                      priority
                      quality={100}
                    />
                    {item.old_price === null || reduction(item.old_price, item.new_price) === 0 ? null : (
                      <p className="bg-gold absolute top-3 left-3 rounded-full shadow-lg px-2 py-4 text-secondary font-semibold">
                        -{reduction(item.old_price, item.new_price)}%
                      </p>
                    )}
                  </div>
                  <div className="py-2 mb-2 flex flex-col gap-2 px-3 ">
                    <div className="flex justify-between flex-wrap">
                      <h2 className="font-semibold text-secondary text-lg lg:text-xl group-hover:text-yel">
                        {new Intl.NumberFormat('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }).format(+item.new_price)} 
                        <span className="text-base"> {tm('DA')} </span>
                      </h2> 
                      {item.instock === "no" && (
                        <p className="inline-block bg-red-500 text-white text-xs font-bold p-1.5 rounded-sm shadow-sm ">
                          {t('off')}
                        </p>
                      )}
                    </div>

                    <div>
                      {reviews.filter(post => post.product === item.id).length ? 
                        <div className='flex gap-2 items-center mb-1'>
                          <StarRating rating={(reviews.reduce((sum:any, review:any) => sum + +review.rating, 0)) / reviews.length} size={16}/>   
                          <p className='font-light text-sm text-gold group-hover:text-white'>{reviews.filter(post => post.product === item.id).length}</p>   
                        </div>
                      : ""}
                      <h2 className="text-sm md:text-base text-gray-600 group-hover:text-yel">{getLocalizedField(item, "title").slice(0,30)+"..."}</h2>
                      <div className="flex justify-between flex-wrap">
                        {item.old_price != null && !isNaN(+item.old_price) && (
                          <p className="text-bl font-semibold font-playfair capitalize text-xs sm:text-sm flex gap-1 items-center">
                            {t('save')} {" "}
                                {new Intl.NumberFormat('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        }).format((Number(item.old_price) - Number(item.new_price)))} 
                           <span> {tm("DA")}</span>
                            <HiTrendingDown size={16} />
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {sortedProducts.length < itemsPerPage ? "" : (
            <div className="flex justify-between items-center gap-2 mb-50 pb-50 mx-4 mt-4 flex-wrap">
              {currentPage === 1 ? (
                <div className="flex items-center cursor-pointer text-gold select-none">
                  {l == 'ar' ? <MdArrowBackIos className="rotate-180" /> : <MdArrowBackIos />}
                  <p className="capitalize font-semibold">{t('previous')}</p>
                </div>
              ) : (
                <div className="flex items-center cursor-pointer text-b hover:text-gold" onClick={handlePrevious}>
                  {l == 'ar' ? <MdArrowBackIos className="rotate-180" /> : <MdArrowBackIos />}
                  <p className="capitalize font-semibold">{t('previous')}</p>
                </div>
              )}

              {/* Page Numbers */}
              <div className="flex gap-2 flex-wrap">
                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  const isActive = currentPage === page;

                  return (
                    <div
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`
                        px-3 py-1 rounded-2xl cursor-pointer font-bold transition
                        ${isActive
                          ? "bg-gold text-b scale-110 border border-1 border-b"
                          : "bg-b text-gold hover:bg-b hover:text-yel"}
                      `}
                    >
                      {page}
                    </div>
                  );
                })}
              </div>

              {/* Next Button */}
              {currentPage === totalPages ? (
                <div className="flex items-center cursor-pointer text-black select-none">
                  <p className="capitalize font-semibold">{t('next')}</p>
                  {l == 'ar' ? <MdArrowBackIos /> : <MdArrowBackIos className="rotate-180" />}
                </div>
              ) : (
                <div className="flex items-center cursor-pointer text-n hover:text-gold" onClick={handleNext}>
                  <p className="capitalize font-semibold">{t('next')}</p>
                  {l == 'ar' ? <MdArrowBackIos /> : <MdArrowBackIos className="rotate-180" />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}